<script setup lang="ts">
  import { computed, inject, ref, useSlots } from 'vue'
  import { useRouter } from 'vue-router'
  import { track } from '@/helpers/mixpanelDes'
  import { getLabel } from '@/helpers/product'
  import { menuListboxKey } from '@/constants/menu'
  import DesBadge from '@/components/DesBadge.vue'

  type TMenuItem = {
    selected?: boolean
    danger?: boolean
    icon?: string | string[]
    image?: string
    description?: string
    tip?: string
    shortcut?: string
    more?: boolean
    external?: boolean
    to?: string
    multiline?: number
    event?: string
    unlockedFor?: number
    disabled?: boolean
    value?: string
  }

  const props = defineProps<TMenuItem>()
  const clampClass = ref<string>(`line-clamp-${props.multiline}`)
  const router = useRouter()
  const slots = useSlots()
  const listbox = inject(menuListboxKey, null)

  const itemAttrs = computed(() => {
    const list = listbox?.value

    return {
      'role': list ? 'option' : 'menuitem',
      'aria-selected': list ? props.selected : undefined,
      'aria-disabled': props.disabled || undefined,
      'data-testid': list?.testId && props.value ? `${list.testId}-option-${props.value}` : undefined,
    }
  })

  function trackMenuClick(event: string | null) {
    const slotContent = slots.default ? slots.default() : null
    const label =
      slotContent
        ?.map((vnode) => vnode.children)
        .join('')
        .trim() || 'unknown'

    if (event) {
      track(event, {
        label,
      })
    }
    if (props.to) {
      router.push(props.to)
    }
  }
</script>

<template>
  <a
    v-bind="itemAttrs"
    @click="trackMenuClick(event ? event : null)"
    class="px-2 py-1 cursor-pointer rounded-lg flex hover:bg-white-gray select-none w-full"
    :class="[
      description ? 'items-start gap-4' : 'items-center gap-2',
      danger ? 'text-red-60' : 'text-blue-10',
      disabled ? 'pointer-events-none opacity-50' : '',
    ]"
    :menu-toggle-ignore="more || undefined"
  >
    <des-icon v-if="icon" :icon="icon" class="h-icon w-icon" :class="{ 'mt-0.5': description }" />
    <img v-else-if="image" :src="image" class="h-8 self-center" alt="" />
    <div class="flex flex-col grow shrink overflow-hidden">
      <div
        class="grow shrink text-ellipsis gap-2"
        :class="{
          'font-medium text-sm': description,
          'overflow-hidden whitespace-nowrap flex': !multiline,
          [clampClass]: multiline,
        }"
      >
        <slot></slot>
      </div>
      <div class="grow text-xs text-black-40 shrink leading-4" v-if="description">
        {{ description }}
      </div>
    </div>
    <span v-if="shortcut" class="text-xs text-black-40 ml-auto self-center pl-3 whitespace-nowrap">
      {{ shortcut }}
    </span>
    <DesBadge v-if="unlockedFor">{{ getLabel(unlockedFor) }}</DesBadge>
    <des-icon v-if="selected" icon="fa-light fa-check" class="pointer-events-none h-icon w-icon text-blue-50" />
    <des-icon
      v-if="tip"
      icon="fa-light fa-circle-question"
      class="h-icon w-icon text-black-60"
      v-des-tooltip:right="tip"
    />
    <des-icon v-if="more" icon="fa-light fa-chevron-right" class="pointer-events-none h-icon w-icon self-center" />
    <des-icon v-if="external" icon="fa-light fa-arrow-up-right" class="pointer-events-none h-icon w-icon" />
  </a>
</template>
